import { motion } from 'motion/react';

import { cn } from '@april9au/react-common/utils';

import type { SiteSettings } from '@/cms/site-settings';
import { stagedChild } from '@/components/motion/variants';

/**
 * The pieces a screen renders *inside* the flow's persistent card. The card,
 * the header and the photograph behind them belong to
 * `routes/onboarding-layout`, which mounts them once for the whole flow.
 */

/**
 * The display heading: Bebas, uppercase, navy, on the two token sizes the flow
 * uses — `hero` for the welcome pitch and `step` for a question.
 */
export function OnboardingHeading({
  children,
  size = 'step',
  className,
}: {
  children: React.ReactNode;
  size?: 'hero' | 'step';
  className?: string;
}) {
  return (
    <h1
      className={cn(
        'font-bebas text-[length:var(--bp-size)] leading-[var(--bp-leading-display)] tracking-[var(--bp-tracking-display)] uppercase',
        size === 'hero' ? '[--bp-size:var(--bp-text-hero)]' : '[--bp-size:var(--bp-text-step)]',
        className,
      )}
    >
      {children}
    </h1>
  );
}

/**
 * The flow asks three questions — language, age, name — and they are split
 * across three routes: `/` asks the first, `/start` the second and
 * `/h/:theme/profile` the third. The total therefore belongs to none of them
 * and lives here, so the three screens cannot disagree about how long the flow
 * is.
 */
export const ONBOARDING_STEPS = 3;

/**
 * "Step 1 of 3" plus one segment per step. Deliberately a counter and not a
 * navigable stepper: the GOV.UK service pattern warns that multi-step
 * indicators mislead as soon as steps branch, and ours branch — the `/kids`
 * entry skips the language step, and renders no counter at all.
 *
 * Each segment is a grey track with a fill that scales out of the reading edge
 * as the step is reached, so progress *travels* rather than switching colour.
 * `initial={false}` keeps that off the first paint: on arrival the card's
 * entrance is the only motion, and the segments simply start filled.
 *
 * `tone` is which of the flow's two card materials it is sitting on. `ink` is
 * the white `frost` card the question screens use; `inverse` is the welcome
 * screen's navy `bp-glass`, where the navy "done" fill would be invisible and
 * white takes its place. Yellow means "where you are" in both.
 */
export function StepCounter({
  step,
  total,
  label,
  tone = 'ink',
  className,
}: {
  step: number;
  total: number;
  label: string;
  tone?: 'ink' | 'inverse';
  className?: string;
}) {
  const inverse = tone === 'inverse';

  return (
    <motion.p
      {...stagedChild}
      className={cn(
        'flex items-center gap-3 text-xs font-bold tracking-wider uppercase',
        inverse ? 'text-white/70' : 'text-bp-ink-muted',
        className,
      )}
    >
      {label}
      <span aria-hidden className="flex flex-1 items-center gap-1.5">
        {/*
          Behind you, where you are, ahead of you — the same three states as the
          journey's `OverallProgress`, so progress means the same thing in the
          header later as it does here.
        */}
        {Array.from({ length: total }, (_, index) => (
          <span
            key={index}
            className={cn('relative h-1 flex-1 overflow-hidden rounded-full', inverse ? 'bg-white/20' : 'bg-surf-gray')}
          >
            <motion.span
              className={cn(
                'absolute inset-0 origin-left rounded-full rtl:origin-right',
                index < step - 1 ? (inverse ? 'bg-white/85' : 'bg-surf-dark-blue') : 'bg-bp-yellow',
              )}
              initial={false}
              animate={{ scaleX: index < step ? 1 : 0 }}
            />
          </span>
        ))}
      </span>
    </motion.p>
  );
}

/**
 * `site_settings.step_logo` above a question's heading, on the age and name
 * screens. Decorative — the same wordmark is already in the header with a real
 * accessible name — so the image is `alt=""` and adds nothing to the reading
 * order.
 *
 * Centred at every width. The card's content reads as a column and the mark is
 * the only thing above the heading, so hanging it off the start edge left it
 * looking like a stray favicon.
 *
 * Same contract as the welcome hero and the header lockup: a Prismic image has
 * no ratio knowable here, so the box is a fixed height and the heading below
 * cannot move when the bytes land. Both axes are capped and the box takes the
 * full width, which is what lets one field hold either shape the campaign
 * calendar puts in it — the square SLSA roundel, or a full-width seasonal
 * lockup like the 4:1 SLS + Qantas one — without either being cropped or
 * overflowing the card. The height is generous enough that a lockup's fine
 * print survives: at 80 px a 4:1 mark is 320 px wide, which still fits the
 * content column of a 390 px phone.
 */
export function StepLogo({
  src,
  height,
  className,
}: {
  src: string;
  /** `step_logo_height`; `null` keeps the built-in 64/80 px. */
  height: number | null;
  className?: string;
}) {
  return (
    <motion.div
      {...stagedChild}
      className={cn('flex w-full items-center justify-center', height === null && 'h-16 sm:h-20', className)}
      style={height === null ? undefined : { height }}
    >
      {/* Intrinsic size of the default 225 px roundel. `object-contain` fits any other shape. */}
      <img src={src} alt="" width={225} height={225} className="max-h-full max-w-full object-contain" />
    </motion.div>
  );
}

/**
 * "In partnership with" and the partner logo, under the step logo on the age
 * and name screens. Driven by `site_settings`, so a sponsor change is content
 * work. The logo is decorative and the label carries the words.
 */
export function PartnerMark({
  partner,
  fallbackLabel,
  className,
}: {
  /** `siteSettings.partner`. `null` when the sponsorship is off. */
  partner: SiteSettings['partner'];
  /** The translated catalogue label, used when the document leaves its own blank. */
  fallbackLabel: string;
  className?: string;
}) {
  if (partner === null) {
    return null;
  }

  return (
    <motion.p
      {...stagedChild}
      className={cn('text-bp-ink-muted flex items-center justify-center gap-2.5 text-xs font-semibold', className)}
    >
      {partner.label ?? fallbackLabel}
      {/* Fixed height, so the label does not move when the logo loads. */}
      <img
        src={partner.logoUrl}
        alt=""
        width={297}
        height={60}
        className={cn('w-auto object-contain', partner.logoHeight === null && 'h-6 max-w-40')}
        // The width cap scales with the authored height.
        style={
          partner.logoHeight === null
            ? undefined
            : { height: partner.logoHeight, maxWidth: partner.logoHeight * (160 / 24) }
        }
      />
    </motion.p>
  );
}
